import { useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  ListVideo,
  Pause,
  Play,
  Search,
  Square,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import type { GlobePoint, GlobePlaylist } from "#shared/schemas/globe.ts";
import { cn } from "@web/lib/utils.ts";
import { useArtists } from "@web/features/library/api.ts";
import { resolveArtistIdsToTargets } from "@web/features/globe/shotTargets.ts";
import { useGlobeShotStore } from "@web/stores/globeShot.ts";
import { useGlobeStore } from "@web/stores/globe.ts";

/**
 * Batch shot queue — reorder, remove, run sequentially.
 */
export function BatchQueuePanel({
  hidden,
  disabled,
  points,
  playlists,
  onRun,
  onPause,
  onStop,
}: {
  hidden?: boolean;
  disabled?: boolean;
  points: GlobePoint[];
  playlists: GlobePlaylist[];
  onRun: () => void;
  onPause: () => void;
  onStop: () => void;
}) {
  const collapsed = useGlobeShotStore((s) => s.queueCollapsed);
  const setCollapsed = useGlobeShotStore((s) => s.setQueueCollapsed);
  const queue = useGlobeShotStore((s) => s.queue);
  const skipped = useGlobeShotStore((s) => s.skipped);
  const dedupe = useGlobeShotStore((s) => s.dedupeByCountry);
  const setDedupe = useGlobeShotStore((s) => s.setDedupeByCountry);
  const addTargets = useGlobeShotStore((s) => s.addTargets);
  const removeFromQueue = useGlobeShotStore((s) => s.removeFromQueue);
  const reorderQueue = useGlobeShotStore((s) => s.reorderQueue);
  const clearQueue = useGlobeShotStore((s) => s.clearQueue);
  const clearSkipped = useGlobeShotStore((s) => s.clearSkipped);
  const batchStatus = useGlobeShotStore((s) => s.batchStatus);
  const batchProgress = useGlobeShotStore((s) => s.batchProgress);
  const batchSummary = useGlobeShotStore((s) => s.batchSummary);
  const setBatchSummary = useGlobeShotStore((s) => s.setBatchSummary);
  const selectedPlaylistIds = useGlobeStore((s) => s.selectedPlaylistIds);
  const artists = useArtists();
  const [query, setQuery] = useState("");

  const nameMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of artists.data ?? []) {
      m.set(a.id, a.name ?? a.id);
    }
    return m;
  }, [artists.data]);

  const searchHits = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    return (artists.data ?? [])
      .filter((a) => (a.name ?? "").toLowerCase().includes(q))
      .slice(0, 8);
  }, [artists.data, query]);

  function addArtistIds(ids: string[]) {
    const { targets, skipped: skips } = resolveArtistIdsToTargets(
      ids,
      nameMap,
      points,
    );
    addTargets(targets, skips);
    const skipN = skips.length;
    toast.success(
      `Added ${targets.length} target${targets.length === 1 ? "" : "s"}` +
        (skipN ? ` · ${skipN} skipped` : ""),
    );
  }

  function addFromPlaylists() {
    const ids: string[] = [];
    for (const pid of selectedPlaylistIds) {
      const pl = playlists.find((p) => p.id === pid);
      if (pl) ids.push(...pl.artistIds);
    }
    if (ids.length === 0) {
      toast.message("No playlists selected", {
        description: "Select a playlist on the globe first.",
      });
      return;
    }
    addArtistIds(ids);
  }

  if (hidden) return null;

  if (collapsed) {
    return (
      <div className="pointer-events-auto absolute top-3 right-3 z-20">
        <button
          type="button"
          title="Shot queue"
          aria-label="Expand shot queue"
          disabled={disabled && batchStatus === "idle"}
          className={cn(
            "flex h-8 items-center gap-1.5 rounded-md border border-line bg-panel/90 px-2 text-xs text-muted backdrop-blur-sm",
            "hover:border-accent/40 hover:text-text disabled:opacity-40",
          )}
          onClick={() => setCollapsed(false)}
        >
          <ListVideo className="size-3.5" />
          Queue{queue.length > 0 ? ` · ${queue.length}` : ""}
        </button>
      </div>
    );
  }

  const running = batchStatus === "running" || batchStatus === "paused";

  return (
    <div className="pointer-events-auto absolute top-3 right-3 z-20 flex max-h-[min(70vh,32rem)] w-72 flex-col rounded-md border border-line bg-panel/95 shadow-lg backdrop-blur-sm">
      <div className="flex shrink-0 items-center justify-between gap-1 border-b border-line px-2 py-1.5">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-text">
          <ListVideo className="size-3.5 text-accent" />
          Queue · {queue.length}
        </div>
        <button
          type="button"
          aria-label="Collapse queue"
          className="flex size-6 items-center justify-center rounded text-faint hover:bg-row-hover hover:text-text"
          onClick={() => setCollapsed(true)}
        >
          <ChevronUp className="size-3.5" />
        </button>
      </div>

      <div className="shrink-0 space-y-1.5 border-b border-line p-2">
        <div className="relative">
          <Search className="pointer-events-none absolute top-1.5 left-1.5 size-3.5 text-faint" />
          <input
            type="search"
            value={query}
            disabled={running}
            placeholder="Add artist…"
            className="h-7 w-full rounded border border-line bg-panel py-0 pr-2 pl-6 text-xs text-text placeholder:text-faint"
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        {searchHits.length > 0 && (
          <ul className="max-h-28 overflow-y-auto rounded border border-line bg-panel-elevated">
            {searchHits.map((a) => (
              <li key={a.id}>
                <button
                  type="button"
                  disabled={running}
                  className="flex w-full px-2 py-1 text-left text-xs text-muted hover:bg-row-hover hover:text-text disabled:opacity-40"
                  onClick={() => {
                    addArtistIds([a.id]);
                    setQuery("");
                  }}
                >
                  {a.name ?? a.id}
                </button>
              </li>
            ))}
          </ul>
        )}
        <button
          type="button"
          disabled={running}
          className="h-7 w-full rounded border border-line text-xs text-muted hover:text-text disabled:opacity-40"
          onClick={() => addFromPlaylists()}
        >
          Add from selected playlists
        </button>
        <label className="flex items-center gap-2 text-xs text-muted">
          <input
            type="checkbox"
            checked={dedupe}
            disabled={running}
            className="size-3.5 accent-accent"
            onChange={(e) => setDedupe(e.target.checked)}
          />
          Dedupe by country
        </label>
      </div>

      <ul className="min-h-0 flex-1 overflow-y-auto py-1">
        {queue.length === 0 && (
          <li className="px-2 py-3 text-xs text-faint">
            Empty. Search artists, add playlists, or send from Ideas / Artists.
          </li>
        )}
        {queue.map((t, i) => (
          <li
            key={t.id}
            className="flex items-center gap-1 px-1.5 py-1 hover:bg-row-hover"
          >
            <div className="flex shrink-0 flex-col">
              <button
                type="button"
                disabled={running || i === 0}
                aria-label="Move up"
                className="text-faint hover:text-text disabled:opacity-20"
                onClick={() => reorderQueue(i, i - 1)}
              >
                <ChevronUp className="size-3" />
              </button>
              <button
                type="button"
                disabled={running || i === queue.length - 1}
                aria-label="Move down"
                className="text-faint hover:text-text disabled:opacity-20"
                onClick={() => reorderQueue(i, i + 1)}
              >
                <ChevronDown className="size-3" />
              </button>
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-medium text-text">{t.artistName}</p>
              <p className="truncate text-xs text-faint">{t.countryLabel}</p>
            </div>
            <button
              type="button"
              disabled={running}
              aria-label="Remove"
              className="shrink-0 rounded p-1 text-faint hover:text-destructive disabled:opacity-40"
              onClick={() => removeFromQueue(t.id)}
            >
              <X className="size-3.5" />
            </button>
          </li>
        ))}
      </ul>

      {skipped.length > 0 && (
        <div className="shrink-0 border-t border-line px-2 py-1.5 text-xs text-faint">
          <div className="flex items-center justify-between">
            <span>{skipped.length} skipped (no country / dedupe)</span>
            <button
              type="button"
              className="text-muted hover:text-text"
              onClick={() => clearSkipped()}
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {batchProgress && running && (
        <div className="shrink-0 border-t border-line px-2 py-1.5 text-xs text-muted">
          {batchProgress.index} / {batchProgress.total} · {batchProgress.artistName} ·{" "}
          {batchProgress.countryLabel}
          {batchStatus === "paused" ? " · paused" : ""}
        </div>
      )}

      {batchSummary && batchStatus === "done" && (
        <div className="shrink-0 border-t border-line px-2 py-1.5 text-xs text-muted">
          <p>
            Done: {batchSummary.recorded} recorded, {batchSummary.skipped} skipped
          </p>
          <p className="text-faint">
            Preset {batchSummary.presetName}
            {batchSummary.outputLabel ? ` · ${batchSummary.outputLabel}` : ""}
          </p>
          <button
            type="button"
            className="mt-0.5 text-muted hover:text-text"
            onClick={() => setBatchSummary(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="flex shrink-0 gap-1 border-t border-line p-2">
        {batchStatus === "idle" || batchStatus === "done" ? (
          <button
            type="button"
            disabled={disabled || queue.length === 0}
            className={cn(
              "flex h-7 flex-1 items-center justify-center gap-1 rounded bg-accent text-xs font-semibold text-accent-contrast",
              "hover:bg-accent-hover disabled:opacity-40",
            )}
            onClick={() => onRun()}
          >
            <Play className="size-3.5" />
            Run
          </button>
        ) : (
          <>
            <button
              type="button"
              className="flex h-7 flex-1 items-center justify-center gap-1 rounded border border-line text-xs text-muted hover:text-text"
              onClick={() => (batchStatus === "paused" ? onRun() : onPause())}
            >
              {batchStatus === "paused" ? (
                <>
                  <Play className="size-3.5" />
                  Resume
                </>
              ) : (
                <>
                  <Pause className="size-3.5" />
                  Pause
                </>
              )}
            </button>
            <button
              type="button"
              className="flex h-7 flex-1 items-center justify-center gap-1 rounded border border-line text-xs text-muted hover:text-destructive"
              onClick={() => onStop()}
            >
              <Square className="size-3.5" />
              Stop
            </button>
          </>
        )}
        <button
          type="button"
          disabled={running || queue.length === 0}
          aria-label="Clear queue"
          className="flex size-7 items-center justify-center rounded border border-line text-faint hover:text-destructive disabled:opacity-40"
          onClick={() => clearQueue()}
        >
          <Trash2 className="size-3.5" />
        </button>
      </div>
    </div>
  );
}
